import {getNetPrice, getPriceInfo} from "~/feature/ProductDescriptor/v1/productDescriptorUtils";
import type {NetPriceData} from "~/models/PriceData";
import type {RootState} from "~/redux/store";
import {selectProductDescriptor} from "~/redux/products/productsSelectors";
import {selectProperAmountInCart} from "~/AddToCart/redux/cartAmounts/cartAmountSelectors";
import type {ProductDescriptor} from "~/feature/ProductDescriptor/v1/types";

interface GetNetPriceProps {
  totalQuantity: number;
  product: ProductDescriptor;
  packCode: string;
}

interface SelectNetPriceProps {
  extraQuantity?: number;
  productCode: string;
  packCode: string;
  priceReference: string;
}

export type NetPriceType = "netPrice" | "salesPrice";

const NO_DATA = {
  netPrice: null,
  currentPrice: null,
  currentPriceType: "netPrice",
  salesPrice: null,
  isSalesPriceSame: true,
} as const;

export function selectNullableBothProductPrices(props: SelectNetPriceProps) {
  return (state: RootState) => {
    if (!props.productCode) {
      return NO_DATA;
    }
    const product = selectProductDescriptor(state, props);
    if (!product) {
      return NO_DATA;
    }
    return selectBothProductPrices(props)(state);
  };
}

export function selectBothProductPrices(props: SelectNetPriceProps) {
  return (state: RootState) => {
    const product = selectProductDescriptor(state, props);
    const cartQty = selectProperAmountInCart(props)(state);
    const totalQuantity = cartQty + (props.extraQuantity ?? 0);

    return getBothProductPrices({ totalQuantity, product, packCode: props.packCode });
  };
}

export function getBothProductPrices(props: GetNetPriceProps) {
  const netPrice = getNetPrice(props);

  let salesPrice = null;
  const priceInfo = getPriceInfo(props);
  if (priceInfo) {
    const salesPriceData = priceInfo.salesCampaign?.[props.packCode]?.[0]?.actualSalesPrice;
    if (salesPriceData) {
      salesPrice = makeNetPrice(salesPriceData, netPrice);
    }
  }
  if (!salesPrice) {
    return {
      netPrice,
      currentPrice: netPrice,
      currentPriceType: "netPrice",
      salesPrice: null,
      isSalesPriceSame: false,
    } as const;
  }

  const isRegularLower = netPrice.value < salesPrice.value;

  return {
    netPrice,
    salesPrice,
    currentPrice: isRegularLower ? netPrice : salesPrice,
    currentPriceType: isRegularLower ? "netPrice" : "salesPrice",
    isSalesPriceSame: salesPrice.value === netPrice.value,
  } as const;
}

function makeNetPrice(price: PriceData, netPrice: NetPriceData): NetPriceData {
  return {
    ...netPrice,
    baseValue: price.value,
    value: price.value * (1 + netPrice.surcharge),
  };
}
